// ── agent identity · one contract ────────────────────────────────────────────
//
// How a person wants their AI to talk, answer and behave, and the habits it
// keeps for them without being asked ("before you ask me anything, sum up what
// was done and what was asked"). A person keeps one or more identities: one for
// every agent, or one for a tool or a named agent of theirs. innernet learns
// lines for them from their memory (offered, never applied until kept) and hands
// the right identity to every AI that reads through the door, so whichever model
// they talk to, the experience is the same one.
//
// Pure and client-safe.

/** who an identity is for: every agent, or these tools / agents (lowercase keys:
 *  claude · chatgpt · cursor · gemini · copilot · codex · netti · an agent's slug) */
export type IdentityFor = string[];

/** where a line sits: how it talks · answers · behaves, or a habit it keeps */
export type IdentityField = 'talks' | 'answers' | 'behaves' | 'habit';

/** where a learned line came from: what the person said, when, and through which app.
 *  Shown only to the person; the door never hands it to an ai. */
export interface IdentitySource {
  /** the self_facts row it rests on, when there is one */
  fact?: string | null;
  /** when it was said (the fact's time) */
  at: string;
  /** the app it came through ("claude", "chatgpt"…), when known */
  app?: string | null;
  /** `noticed`: netti caught it as it arrived · `learned`: read on a "learn" press */
  how: 'noticed' | 'learned';
}

/** a line the identity keeps: a habit, or (field) one more line of how it talks, answers or behaves */
export interface IdentityHabit {
  id: string;
  /** the line, as an instruction to the agent ("before asking me anything, …") */
  text: string;
  /** where it sits; absent = a habit (every line before 20 oct is one) */
  field?: Exclude<IdentityField, 'habit'> | null;
  /** `you`: written by the person · `learned`: kept from what netti learned */
  from: 'you' | 'learned';
  /** a learned line: the memory it rests on, in a short line (never a private fact) */
  because?: string | null;
  /** a learned line: where it came from */
  source?: IdentitySource | null;
  on: boolean;
}

/** a line netti learned from the person's memory, waiting to be kept or let go */
export interface IdentitySuggestion {
  id: string;
  /** where it would go */
  field: IdentityField;
  text: string;
  /** what in their memory it rests on, in a short line */
  because: string;
  /** where it came from (absent on offers made before 20 oct) */
  source?: IdentitySource | null;
}

export interface AgentIdentity {
  id: string;
  name: string;
  /** [] = every agent */
  for: IdentityFor;
  /** how it talks: voice, tone, words */
  talks: string;
  /** how it answers: length, shape, format */
  answers: string;
  /** how it behaves: what it does and never does */
  behaves: string;
  habits: IdentityHabit[];
  suggestions: IdentitySuggestion[];
  /** when netti last read the memory for this identity */
  learnedAt: string | null;
  updatedAt: string;
}

/** the tools an identity can be for, in the order the picker shows them */
export const IDENTITY_TOOLS: readonly { id: string; label: string }[] = [
  { id: 'claude', label: 'Claude' },
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'cursor', label: 'Cursor' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'copilot', label: 'Copilot' },
  { id: 'codex', label: 'Codex' },
  { id: 'netti', label: 'netti' },
];

export const MAX_IDENTITIES = 12;
/** every kept line an identity holds: habits and learned lines together */
export const MAX_HABITS = 40;
/** offers waiting on one identity */
export const MAX_WAITING = 12;
/** what one identity may hold waiting at most: a move-in brings a whole instruction file at once (lib/intake/land);
 *  netti's own notices still stop at MAX_WAITING */
export const MAX_WAITING_STORED = 40;
export const MAX_FIELD = 800;
export const MAX_HABIT = 280;

/** what the door hands an AI: the identity as plain lines, nothing else */
export interface IdentityBrief {
  name: string;
  lines: string[];
  note: string;
}

// ── personalities, mixed (MOVING-IN-PLAN.md §8, migration 164) ───────────────
//
// An identity is a personality. An agent, a connection or a key can take an
// ordered list of them, mixed in that order (compose composeIdentities): the
// list lives beside the identities on the same settings row
// (user_settings.identity_mix), keyed by what it is for, so `for` keeps meaning
// what it always meant (tool keys, or every agent) and one personality can sit
// in two lists in two different places.

/** what a list is mixed for: one agent, one connection, one key (by row id) */
export type MixKind = 'agent' | 'connection' | 'key';

/** "agent:<agents.id>" · "connection:<connections.id>" · "key:<api_keys.id>" → personality ids, in mix order */
export type IdentityMix = Record<string, string[]>;

/** the most personalities one agent, connection or key mixes */
export const MAX_MIX = 6;
/** the most agents, connections and keys that carry a list of their own */
export const MAX_MIX_TARGETS = 300;
